/**
 * Leadership School page: Школа лидерства — events from API by category "training".
 * Filtered by title "Школа лидерства", Wed 19:00, Phuket Town.
 */

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { ListSkeleton } from "../../components/ui/Skeleton.tsx";
import { EventCard } from "../../components/EventCard.tsx";
import {
  IconClock,
  IconMapPin,
  IconChevronRight,
} from "../../components/Icons.tsx";
import { ErrorDisplay } from "../../components/ErrorDisplay.tsx";
import {
  graphql,
  withMinDelay,
  EVENTS_QUERY,
  CATEGORY_BY_CODE_QUERY,
  type Event,
  type EventWithContent,
} from "../../lib/graphql.ts";
import {
  getDateKeyBangkok,
  getTodayDateKeyBangkok,
  formatDateShortBangkok,
  getDayOfMonthAndWeekdayBangkok,
  addDaysToDateKey,
} from "../../lib/bangkok-time.ts";
import { APP_TIMEZONE } from "../../config.ts";
import { getTelegram } from "../../lib/telegram.ts";
import { sanitizeUrl } from "../../lib/url-utils.ts";

const WEEKDAYS_FULL = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
];

const LEADERS_SCHOOL_TITLE = "Школа лидерства";

interface TrainingCategory {
  id: string;
  name: string;
  code: string;
  color: string | null;
}

function buildImageUrl(url: string | null | undefined): string | null {
  if (!url?.trim()) return null;
  if (url.startsWith("http")) return url;
  return url.startsWith("/") ? url : `/${url}`;
}

export function LeadershipSchoolPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const [category, setCategory] = useState<TrainingCategory | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  useEffect(() => {
    let cancelled = false;
    setCategory(null);
    setError(null);
    graphql<{ categoryByCode: TrainingCategory | null }>(
      CATEGORY_BY_CODE_QUERY,
      { code: "training" }
    )
      .then((data) => {
        if (cancelled) return;
        setCategory(data.categoryByCode ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Ref to track whether the component is mounted for async safety
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadEvents = useCallback(() => {
    if (!category?.id) return;
    setIsLoadingEvents(true);
    setError(null);
    const fromDate = getTodayDateKeyBangkok();
    const toDate = addDaysToDateKey(fromDate, 60);
    withMinDelay(
      graphql<{ events: Event[] }>(EVENTS_QUERY, {
        categoryIds: [category.id],
        timezone: APP_TIMEZONE,
        fromDate,
        toDate,
      })
    )
      .then((data) => {
        if (!isMountedRef.current) return;
        setEvents(data.events ?? []);
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        setError(err);
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setIsLoadingEvents(false);
      });
  }, [category?.id]);

  useEffect(() => {
    if (category?.id) loadEvents();
    else setEvents([]);
  }, [category?.id, loadEvents]);

  const schoolEvents = useMemo(() => {
    const filtered = events.filter((e) => e.title === LEADERS_SCHOOL_TITLE);
    filtered.sort(
      (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
    );
    return filtered;
  }, [events]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const event of schoolEvents) {
      const dateKey = getDateKeyBangkok(event.startsAt);
      const existing = map.get(dateKey);
      if (existing) {
        existing.push(event);
      } else {
        map.set(dateKey, [event]);
      }
    }
    return map;
  }, [schoolEvents]);

  const dayKeys = useMemo(
    () => Array.from(eventsByDay.keys()).sort(),
    [eventsByDay]
  );

  if (error && !isLoadingEvents) {
    return (
      <Layout>
        <ErrorDisplay
          error={error}
          onRetry={category ? loadEvents : () => window.location.reload()}
        />
      </Layout>
    );
  }

  const mainEvent = schoolEvents[0] as EventWithContent | undefined;
  const heroImageUrl =
    buildImageUrl(mainEvent?.imageUrl ?? null) ?? "/content/leaders-school.png";
  const descriptionStr = mainEvent?.description?.trim() ?? "";
  const venueStr = mainEvent?.venue ?? "";
  const hasMaps = Boolean(mainEvent?.mapsUrl);
  const todayDateKey = getTodayDateKeyBangkok();

  return (
    <Layout>
      <h1 className="text-xl font-bold text-slate-800 mb-4">Лидерская школа</h1>

      {/* Hero image */}
      {heroImageUrl ? (
        <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-4 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 bg-slate-100">
          <img
            src={heroImageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      ) : null}

      {/* Section: Школа лидерства */}
      <section className="mb-8">
        <h2 className="text-base font-semibold text-slate-800 mb-2">
          Школа лидерства
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          Ср 19:00 · {venueStr || "Пхукет Таун"}
        </p>

        {descriptionStr ? (
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-4 xs:p-5 mb-4">
            <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap prose prose-slate prose-sm max-w-none">
              {descriptionStr}
            </p>
          </div>
        ) : null}

        {/* Когда и где */}
        <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-4 xs:p-5 mb-4">
          <p className="text-slate-800 font-medium flex items-center gap-2 mb-2">
            <IconClock className="w-5 h-5 text-slate-500 flex-shrink-0" />
            Ср 19:00
          </p>
          {venueStr ? (
            <p className="text-slate-700 flex items-center gap-2 mb-2">
              <IconMapPin className="w-5 h-5 text-slate-500 flex-shrink-0" />
              {venueStr}
            </p>
          ) : null}
          {hasMaps && mainEvent?.mapsUrl && sanitizeUrl(mainEvent.mapsUrl) ? (
            <a
              href={sanitizeUrl(mainEvent.mapsUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-accent)] no-underline"
            >
              Открыть карту
              <IconChevronRight className="w-4 h-4" />
            </a>
          ) : null}
        </div>

        {/* Ближайшие встречи */}
        <p className="section-title px-0 mb-3">Ближайшие встречи</p>
        {isLoadingEvents ? (
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-6">
            <ListSkeleton count={3} />
          </div>
        ) : dayKeys.length > 0 ? (
          <div className="space-y-6">
            {dayKeys.map((dateKey) => {
              const dayEvents = eventsByDay.get(dateKey) ?? [];
              const { weekdayIndex } = getDayOfMonthAndWeekdayBangkok(dateKey);
              const dayName = WEEKDAYS_FULL[weekdayIndex];
              const dateStr = formatDateShortBangkok(dateKey);
              const isToday = dateKey === todayDateKey;

              return (
                <section key={dateKey}>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">
                      {isToday ? "Сегодня" : dayName}
                      <span className="font-normal text-slate-400 ml-2">
                        {dateStr}
                      </span>
                    </span>
                  </div>
                  <div className="space-y-3">
                    {dayEvents.map((event) => (
                      <EventCard key={event.id} event={event} hideTitle />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden bg-white/80 backdrop-blur-sm shadow-sm p-6 text-center border border-slate-100">
            <p className="text-slate-600 text-sm">
              Ближайшие встречи школы лидерства появятся в расписании.
            </p>
          </div>
        )}
      </section>

      {/* Link to full schedule */}
      <div className="mt-6">
        <Link
          to="/schedule"
          className="flex items-center justify-between gap-3 py-4 px-4 rounded-xl bg-white/95 shadow-sm border border-slate-100 active:bg-slate-50 transition-colors no-underline text-inherit"
        >
          <span className="font-medium text-slate-800">
            Всё расписание на неделю
          </span>
          <IconChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
        </Link>
      </div>

      <div className="h-4" />
    </Layout>
  );
}
